import React, { ReactNode, useState } from "react";
import { clsx } from 'clsx';
import './Collapsible.scss';

type CollapsibleProps = {
    title: string,
    openIcon?: string,
    closeIcon?: string,
    titleClass?: string,
    contentClass?: string,
    iconPosition?: string,
    children: ReactNode,
    active?: boolean,
    disabled?: boolean,
    desktopDisabled?: boolean,
    nested?: boolean,
    rotateIcon?: boolean,
    tier?: 1 | 2 | undefined,
    background?: string,
    className?: string,
    readMore?: boolean,
    selectedBackground?: string,
    selectedTextColor?: string,
    useLineBreak?: boolean,
    defaultOpen?: boolean,
    allowMultipleOpen?: boolean
};

export function Collapsible(props: CollapsibleProps) {
  const {
    title,
    active = false,
    openIcon = 'icon-chevron-down',
    closeIcon = 'icon-chevron-up',
    titleClass = 'b2',
    contentClass = 'b2',
    iconPosition = 'right',
    children,
    disabled = false,
    desktopDisabled = false,
    nested = false,
    rotateIcon = false,
    tier = null,
    background = 'transparent',
    className = '',
    readMore = false,
    selectedBackground,
    selectedTextColor,
    useLineBreak = false,
    defaultOpen = false,
    allowMultipleOpen = false
  } = props;

  const [isActive, setIsActive] = useState(active || defaultOpen);

  function toggle() {
    if (disabled) return;

    setIsActive(!isActive);
  }

  // Generate dynamic styles for the selected/active state
  const dynamicStyles = {
    backgroundColor: isActive && selectedBackground ? selectedBackground : background,
    color: isActive && selectedTextColor ? selectedTextColor : undefined,
    borderBottom: useLineBreak ? '1px solid #e5e7eb' : 'none'
  };

  // Render icon based on position and type
  const renderIcon = () => {
    if (iconPosition === 'none' || disabled) return null;

    if (rotateIcon) {
      return (
        <span className={`${openIcon} collapsible__multi-icon transition-transform duration-200 origin-center ${
          isActive ? "rotate-180" : "rotate-0"
        }`}></span>
      );
    } else {
      return (
        <>
          <span className={`collapsible__expand-icon ${openIcon} ${isActive ? 'hidden' : 'block'}`}></span>
          <span className={`collapsible__collapse-icon ${closeIcon} ${isActive ? 'block' : 'hidden'}`}></span>
        </>
      );
    }
  };

  return (
      <div
          className={clsx({
              'collapsible': true,
              'collapsible--active-nested-1': tier == 1 && isActive,
              'collapsible--disabled-nested-1': tier == 1 && disabled,
              'collapsible--active-nested-2': tier == 2 && isActive,
              'collapsible--disabled-nested-2': tier == 2 && disabled,
              'collapsible--active': !tier && isActive,
              'collapsible--disabled': !tier && disabled,
              'collapsible--desktop-disabled': desktopDisabled,
              'collapsible--with-line-break': useLineBreak,
              'collapsible--icon-left': iconPosition === 'left',
              'collapsible--icon-right': iconPosition === 'right',
              'collapsible--no-icon': iconPosition === 'none'
          }, className)}
          style={dynamicStyles}
      >
          <div
              className={`collapsible__title ${titleClass}`}
              onClick={() => toggle()}
          >
            {iconPosition === 'left' && (
              <div className="collapsible__left-icon-wrapper">
                {renderIcon()}
              </div>
            )}

            <div className={'collapsible__title-wrapper'}>
              {title}
            </div>

            {iconPosition === 'right' && (
              <div className="collapsible__right-icon-wrapper">
                {renderIcon()}
              </div>
            )}
          </div>

          <div className={clsx({
              [`collapsible__content ${contentClass}`]: !tier,
              'collapsible__content-nested-1': tier == 1,
              'collapsible__content-nested-2': tier == 2,
              'collapsible__content--read-more': readMore
          })}>
              {children}
          </div>
      </div>
  )
};